<script>
    import { page } from '$app/stores';
    import { invalidateAll } from '$app/navigation';
    import { PUBLIC_RECAPTCHA_SITE_KEY } from '$env/static/public';
    import { version } from '$app/environment';
    //import * as Sentry from '@sentry/sveltekit';
    import * as Sentry from '@sentry/svelte';


    import { source } from 'sveltekit-sse';

    import { enableCustomLogging } from '$lib/console.js';
    import { activeUserData, loadActiveUserData, patchActiveUserDataAsync } from '$lib/app/activeUserData.svelte.js';
    import { server_name } from '$lib/app/lib.js';
    import { uuid, set_uuid } from '$lib/app/uuid.js';
    import { is_uuid } from '$lib/universal/lib.js';


    let noindex = $derived(import.meta.env.DEV || $page.data?.meta?.noindex);
    let title = $derived($page.data?.meta?.title ?? "Visited cities map");
    let description = $derived($page.data?.meta?.description ?? "Scratch Travel Map online. Pin cities/towns/islands/airports/etc you've been to, and get stats of visited countries/regions. Share your visited countries map on your blog/social networks");
    let og_image = $derived($page.data?.meta?.og_image ?? server_name("/i/map.png"));

    let { data, children } = $props();

    enableCustomLogging();

    $effect(() => {
        if(activeUserData.data?.userpage != data.session?.userpage) {
            if(data.session?.userpage) {
                loadActiveUserData(data.session.userpage);
            } else {
                loadActiveUserData(undefined);          // This will remove activeUserData
            }
        }
    });

    let unsubscribe;

    $effect(() => {
        if(data.session?.userpage && !unsubscribe && !uuid) {
            const formData = new FormData();
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            formData.append('device-info', JSON.stringify({
                userAgent: navigator.userAgent,
                platform: [
                        navigator.platform,
                        navigator.deviceMemory ? navigator.deviceMemory + "Gb" : undefined,
                        navigator.hardwareConcurrency ? navigator.hardwareConcurrency + "vCPU" : undefined,
                    ].filter(v => v !== undefined).join(', '),
                languages: ((Ls) => Array.isArray(Ls) ? Ls.join(", ") : undefined) (navigator.languages),
                screenResolution: `${screen.width}x${screen.height}`,
                timeZone, // Example: "America/New_York"
                version,
            }));

            unsubscribe = source(server_name(`/_api/sse/updates`), {
                options: {
                    body: formData,
                    //headers: {
                    //    Authorization: 'Bearer ...',
                    //},
                },
                close({ connect }) {
                    if(data.session?.userpage) {
                        console.log('reconnecting SSE disabled!...');
                        //connect();
                    }
                },
            })
                .select('message')
                .subscribe((value) => {
                    let json;
                    try {
                        json = JSON.parse(value);
                    } catch(error) {
                        if(value) {
                            console.error(`Could not parse "${value} (${typeof value})" as json.`, error);
                        }
                    }
                    if(json && typeof json === 'object') {
                        if (json.logged_out) {
                            console.info('Logged out by server');
                            invalidateAll();
                        } else if(is_uuid(json.uuid)) {
                            set_uuid(json.uuid);
                        } else if(typeof json.delta === 'object' &&  json.delta !== null) {
                            patchActiveUserDataAsync(json.delta)
                                .catch((err) => {
                                    Sentry.captureMessage('patchActiveUserDataAsync', { extra: { err } });
                                    console.error('patchActiveUserDataAsync: error');
                                });
                        } else if(typeof json.map_settings === 'object' &&  json.map_settings !== null) {
                            console.log('got map_settings: '+JSON.stringify(json.map_settings))
                            activeUserData.data.map_settings = json.map_settings;
                        }

                        if(json?.uuid === null) {
                            console.info('Got uuid === null. Erasing uuid');
                            set_uuid(json.uuid);
                        }
                    }
                });
        } else if(unsubscribe) {
            unsubscribe();
            set_uuid(null);
            unsubscribe = undefined;
        }
    });

</script>

<svelte:head>
    <title>{title}</title>
    <meta name="description" content="{description}" />
    <!--{$canonical}-->
    
    {#if import.meta.env.DEV && !$page.data?.meta?.noindex}
    {@html '<!-- WILL BE index,follow ON PROD -->'}
    {/if}

    {#if noindex}
    <meta name=robots content='noindex,nofollow'/>
    {:else}
    {@html '<!-- index,follow -->'}
    {/if}

    <!--link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Fira+Sans+Condensed:wght@400"-->
    <meta property="og:title" content="{title}" />
    <meta property="og:description" content="{description}" />
    <meta property="og:image" content="{og_image}" />
    <meta name="twitter:card" content="summary_large_image"/>
    <meta name="twitter:title" content="{title}"/>
    <meta name="twitter:description" content="{description}"/>
    <meta name="twitter:image" content="{og_image}"/>
    <link rel="icon" type="image/png" sizes="96x96" href="/i/icons8-flag-doodle-96.png">
    <link rel="icon" type="image/png" sizes="32x32" href="/i/icons8-flag-doodle-32.png">
    <link rel="icon" type="image/png" sizes="16x16" href="/i/icons8-flag-doodle-16.png">      
    <link rel="apple-touch-icon" sizes="96x96" href="/i/icons8-flag-doodle-96.png">
    <script src="https://www.google.com/recaptcha/api.js?render={PUBLIC_RECAPTCHA_SITE_KEY}"></script>
    <!-- Google tag (gtag.js) -->
    <script async src="https://www.googletagmanager.com/gtag/js?id=G-TMF86Q4QPJ"></script>
    <script>
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-TMF86Q4QPJ');
    </script>    
    <!-- Google tag (gtag.js) -->
</svelte:head>
{@render children?.()}
